/**
 * Date formatting utilities using Intl.DateTimeFormat for proper localization
 */

const DEFAULT_LOCALE = 'ru-RU'
const FALLBACK_LOCALE = 'en-US'

/**
 * Formats a timestamp (in seconds) to a localized date string
 * @param timestamp - Unix timestamp in seconds (as string or number)
 * @param locale - Locale for formatting (defaults to 'ru-RU')
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export function formatDate(
  timestamp: string | number,
  locale: string = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  },
): string {
  try {
    const date = new Date(Number(timestamp))

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp provided to formatDate:', timestamp)
      return ''
    }

    return new Intl.DateTimeFormat(locale, options).format(date)
  } catch (error) {
    console.warn('Error formatting date:', error)

    // Fallback to default locale if provided locale is invalid
    try {
      const date = new Date(Number(timestamp))
      return new Intl.DateTimeFormat(FALLBACK_LOCALE, options).format(date)
    } catch (fallbackError) {
      throw new Error(
        `Failed to format date with fallback locale: ${fallbackError}`,
      )
    }
  }
}

/**
 * Formats a timestamp to a date and time string
 * @param timestamp - Unix timestamp in seconds (as string or number)
 * @param locale - Locale for formatting (defaults to 'ru-RU')
 * @returns Formatted date and time string
 */
export function formatDateTime(
  timestamp: string | number,
  locale: string = DEFAULT_LOCALE,
): string {
  return formatDate(timestamp, locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Formats a timestamp to a date and time string with local timezone
 * @param timestamp - Unix timestamp in seconds (as string or number)
 * @param locale - Locale for formatting (defaults to 'ru-RU')
 * @returns Formatted date and time string with timezone indicator
 */
export function formatDateTimeWithTimezone(
  timestamp: string | number,
  locale: string = DEFAULT_LOCALE,
): string {
  return formatDate(timestamp, locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

/**
 * Formats a timestamp to a short date string (day.month.year)
 * @param timestamp - Unix timestamp in seconds (as string or number)
 * @param locale - Locale for formatting (defaults to 'ru-RU')
 * @returns Formatted short date string
 */
export function formatShortDate(
  timestamp: string | number,
  locale: string = DEFAULT_LOCALE,
): string {
  return formatDate(timestamp, locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Gets the appropriate locale from app context or browser
 * This can be extended to work with the app's language context
 * @param appLanguage - Current app language from context
 * @returns Appropriate locale string
 */
export function getDateLocale(appLanguage?: string): string {
  if (appLanguage) {
    // Map app language codes to locale codes
    const languageToLocaleMap: Record<string, string> = {
      ru: 'ru-RU',
      en: 'en-US',
    }

    return languageToLocaleMap[appLanguage] || DEFAULT_LOCALE
  }

  // Fallback to browser locale or default
  try {
    return navigator.language || DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}
